import { FileNameSeparatorWithExtension } from '@/components/FileNameSeparatorWithExtension';
import { SearchedAdminInvoiceReportPaymentStatus } from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import {
  ActionCell,
  InfoTagCell,
  StatusCell,
  TooltipCell,
} from '@/features/admin_invoice_reports/components/List/Table/Cells';
import type { AdminInvoiceReportColumn } from '@/features/admin_invoice_reports/types';
import { useTranslation } from '@/i18n';
import { formatZeroAmount } from '@/utils/formatAmount';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  Checkbox,
  IconDrawer,
  TableProps,
  Tag,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import classnames from 'classnames/bind';
import { format } from 'date-util';
import React, { useCallback, useMemo } from 'react';
import styles from './columns.module.scss';

const cx = classnames.bind(styles);

const SORT_DIRECTIONS: ('descend' | 'ascend')[] = ['descend', 'ascend'];

const RESIZABLE_MIN_WIDTH = 68 as const;

const NUMBER_COLUMN_MESSAGE_TYPES = [
  'report_edoc_scanner_message',
  'duplicate_reports_message',
];

const INVOICE_FILE_MESSAGE_TYPES = ['invoice_file_edoc_scanner_message'];

type ResizableColumn = { defaultWidth: number };

const RESIZABLE_COLUMNS: Record<string, ResizableColumn> = {
  office_member: { defaultWidth: 96 },
  title: { defaultWidth: 220 },
  ap_payee_name: { defaultWidth: 128 },
  total_amount: { defaultWidth: 98 },
  report_approval_names: { defaultWidth: 128 },
};

type Column = TableProps<AdminInvoiceReportColumn>['columns'][number];
type ColumnWithResizableMin = Column & { resizableMinWidth?: number };

export type UseColumnsProps = {
  selectedRowKeys: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (record: AdminInvoiceReportColumn, checked: boolean) => void;
  isAllSelected: boolean;
  isAllDisabled: boolean;
  isLoading?: boolean;
  columnWidths?: Record<string, number>;
  onCancel?: (record: AdminInvoiceReportColumn) => void;
  onDelete?: (record: AdminInvoiceReportColumn) => void;
};

const getColumnWidth = (
  columnKey: string,
  columnWidths: Record<string, number>
): number | undefined => {
  const col = RESIZABLE_COLUMNS[columnKey];
  if (!col) return undefined;
  return columnWidths[columnKey] ?? col.defaultWidth;
};

const isResizable = (columnKey: string): boolean =>
  columnKey in RESIZABLE_COLUMNS;

export const getTotalTableWidth = (
  columnWidths: Record<string, number>
): number => {
  const fixedWidths = Object.entries(styles)
    .filter(([key]) => key.startsWith('column_') && key.endsWith('_width'))
    .filter(
      ([key]) => !isResizable(key.replace('column_', '').replace('_width', ''))
    )
    .reduce((sum, [, val]) => sum + Number(val), 0);

  const resizableWidths = Object.entries(RESIZABLE_COLUMNS).reduce(
    (sum, [key, col]) => sum + (columnWidths[key] ?? col.defaultWidth),
    0
  );

  return fixedWidths + resizableWidths;
};

export const useColumns = ({
  selectedRowKeys,
  onSelectAll,
  onSelectRow,
  isAllSelected,
  isAllDisabled,
  isLoading = false,
  columnWidths = {},
  onCancel,
  onDelete,
}: UseColumnsProps): ColumnWithResizableMin[] => {
  const { t } = useTranslation();
  const container = useGlobalContainerRef();

  const sorterConfig = useMemo(
    () =>
      !isLoading
        ? {
            sorter: true,
            sortDirections: SORT_DIRECTIONS,
            showSorterTooltip: { mouseEnterDelay: 0.5 },
          }
        : { showSorterTooltip: false },
    [isLoading]
  );

  const handleCheckboxChange = useCallback(
    (e: CheckboxChangeEvent, record: AdminInvoiceReportColumn) => {
      e.stopPropagation();
      onSelectRow(record, e.target.checked);
    },
    [onSelectRow]
  );

  const getCheckboxTooltipTitle = useCallback(
    (record: AdminInvoiceReportColumn, canSelect: boolean) => {
      if (canSelect) return '';
      if (record.delete_action.reason) return record.delete_action.reason;

      const statusDisplayName = record.is_waiting
        ? t('admin_invoice_reports.table.columns.approving_application_status')
        : record.status?.display_name;
      return `${statusDisplayName}${t(
        'admin_invoice_reports.table.columns.application_number_not_selectable'
      )}`;
    },
    [t]
  );

  const onStopPropagation = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    },
    []
  );

  const allColumns: ColumnWithResizableMin[] = useMemo(
    () => [
      // 1. Checkbox
      {
        title: (
          <Checkbox
            className={cx(isAllDisabled ? 'disabledCustomCheckbox' : '')}
            indeterminate={selectedRowKeys.length > 0 && !isAllSelected}
            checked={isAllSelected}
            disabled={isAllDisabled || isLoading}
            onChange={(e) => onSelectAll(e.target.checked)}
          />
        ),
        dataIndex: 'select',
        key: 'select',
        fixed: 'left',
        onCell: () => ({ onClick: onStopPropagation }),
        render(_, record) {
          const canSelect =
            record.delete_action.allowed && record.delete_action.visible;
          const tooltipTitle = getCheckboxTooltipTitle(
            record,
            record.delete_action.allowed
          );
          return (
            <TooltipCell
              title={tooltipTitle}
              wrapperClassName={cx(styles['columnTooltip'])}
            >
              <Checkbox
                checked={selectedRowKeys.includes(record.id)}
                disabled={!canSelect || isLoading}
                className={cx(!canSelect ? 'disabledCustomCheckbox' : '')}
                onChange={(e: CheckboxChangeEvent) =>
                  handleCheckboxChange(e, record)
                }
              />
            </TooltipCell>
          );
        },
        align: 'center',
        width: `${styles['column_select_width']}px`,
        className: cx(styles['checkboxContainer']),
      },
      // 2. Application number
      {
        title: t('admin_invoice_reports.table.columns.application_number'),
        dataIndex: 'number',
        key: 'number',
        fixed: 'left',
        render(value, record) {
          return (
            <InfoTagCell
              errorMessages={record.error_messages ?? undefined}
              messageType={NUMBER_COLUMN_MESSAGE_TYPES}
            >
              <div className={cx('numberContainer')}>
                {value}
                <Tooltip
                  title={t('open_preview')}
                  getPopupContainer={container}
                  overlayClassName={cx(styles['columnTooltip'])}
                >
                  <div className={cx('iconWrapper')}>
                    <IconDrawer />
                  </div>
                </Tooltip>
              </div>
              <StatusCell status={record.status} />
            </InfoTagCell>
          );
        },
        width: `${styles['column_number_width']}px`,
        ...sorterConfig,
      },
      // 3. Creation date
      {
        title: t('admin_invoice_reports.table.columns.created_date'),
        dataIndex: 'created_at',
        key: 'created_at',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_created_at_width']}px`,
        ...sorterConfig,
      },
      // 4. Application date
      {
        title: t('admin_invoice_reports.table.columns.application_date'),
        dataIndex: 'submitted_at',
        key: 'submitted_at',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_submitted_at_width']}px`,
        ...sorterConfig,
      },
      // 5. Applicant (always shown in admin view)
      {
        title: t('admin_invoice_reports.table.columns.applicant'),
        dataIndex: 'office_member',
        key: 'office_member',
        render(value: { name?: string; number?: string } | undefined) {
          if (!value) return '';
          const tooltipTitle = `${value.name ?? ''} (${value.number ?? ''})`;
          return (
            <TooltipCell
              title={tooltipTitle}
              wrapperClassName={cx(styles['columnTooltip'])}
              className={cx(styles['singleLineTooltipCell'])}
            >
              <div className={cx(styles['applicantContainer'])}>
                <div className={cx(styles['applicantName'])}>
                  {value.name || ''}
                </div>
                <div className={cx(styles['applicantCode'])}>
                  {value.number || ''}
                </div>
              </div>
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('office_member', columnWidths) ??
          `${styles['column_office_member_width']}px`,
        resizable: isResizable('office_member'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 6. Title
      {
        title: t('admin_invoice_reports.table.columns.title'),
        dataIndex: 'title',
        key: 'title',
        render(value) {
          return (
            <TooltipCell
              title={value}
              wrapperClassName={cx(styles['columnTooltip'])}
              lineClamp={2}
            >
              {value || ''}
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('title', columnWidths) ??
          `${styles['column_title_width']}px`,
        resizable: isResizable('title'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 7. Payee
      {
        title: t('admin_invoice_reports.table.columns.payee'),
        dataIndex: 'ap_payee_name',
        key: 'ap_payee_name',
        render(value) {
          return (
            <TooltipCell
              title={value}
              wrapperClassName={cx(styles['columnTooltip'])}
              lineClamp={2}
            >
              {value || ''}
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('ap_payee_name', columnWidths) ??
          `${styles['column_ap_payee_name_width']}px`,
        resizable: isResizable('ap_payee_name'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 8. Trade date
      {
        title: t('admin_invoice_reports.table.columns.expense_booking_date'),
        dataIndex: 'book_date',
        key: 'book_date',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_book_date_width']}px`,
        ...sorterConfig,
      },
      // 9. Payment due date
      {
        title: t('admin_invoice_reports.table.columns.payment_due_date'),
        dataIndex: 'due_date',
        key: 'due_date',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_due_date_width']}px`,
        ...sorterConfig,
      },
      // 10. Total amount
      {
        title: t('admin_invoice_reports.table.columns.total_amount'),
        dataIndex: 'total_amount',
        key: 'total_amount',
        render(value, record) {
          return (
            <>
              <div>{value === '-' ? '' : formatZeroAmount(value) || ''}</div>
              {record.foreign_currency_amount && (
                <div className={cx(styles['foreignCurrencyAmount'])}>
                  {record.foreign_currency_amount}
                </div>
              )}
            </>
          );
        },
        width:
          getColumnWidth('total_amount', columnWidths) ??
          `${styles['column_total_amount_width']}px`,
        align: 'right',
        resizable: isResizable('total_amount'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 11. Approvers
      {
        title: t('admin_invoice_reports.table.columns.approver'),
        dataIndex: 'report_approval_names',
        key: 'report_approval_names',
        render(value) {
          return (
            <TooltipCell
              title={value}
              wrapperClassName={cx(styles['columnTooltip'])}
              lineClamp={2}
            >
              {value || ''}
            </TooltipCell>
          );
        },
        width:
          getColumnWidth('report_approval_names', columnWidths) ??
          `${styles['column_report_approval_names_width']}px`,
        resizable: isResizable('report_approval_names'),
        resizableMinWidth: RESIZABLE_MIN_WIDTH,
      },
      // 12. Approved date
      {
        title: t('admin_invoice_reports.table.columns.approval_date'),
        dataIndex: 'approved_at',
        key: 'approved_at',
        render: (value) => format(value, 'YYYY/MM/DD'),
        width: `${styles['column_approved_at_width']}px`,
        ...sorterConfig,
      },
      // 13. Payment date
      {
        title: t('admin_invoice_reports.table.columns.payment_date'),
        dataIndex: 'payment_date',
        key: 'payment_date',
        render: (value, record) => (
          <div className={cx('paymentDateContainer')}>
            <span>{format(value, 'YYYY/MM/DD')}</span>
            {record.payment_status ===
              SearchedAdminInvoiceReportPaymentStatus.paid && (
              <Tag closable={false} outline={false}>
                {t(
                  'admin_invoice_reports.table.columns.payment_date.paid_label'
                )}
              </Tag>
            )}
          </div>
        ),
        width: `${styles['column_payment_date_width']}px`,
        ...sorterConfig,
      },
      // 14. Received invoice
      {
        title: t('admin_invoice_reports.table.columns.attached_invoice'),
        dataIndex: 'invoice_file',
        key: 'invoice_file',
        render(value, record) {
          if (!value) return '';
          return (
            <div className={cx(styles['invoiceFileContainer'])}>
              <TooltipCell
                title={value.name}
                wrapperClassName={cx(styles['columnTooltip'])}
                className={cx(styles['singleLineTooltipCell'])}
              >
                <FileNameSeparatorWithExtension>
                  {value.name}
                </FileNameSeparatorWithExtension>
              </TooltipCell>
              <InfoTagCell
                errorMessages={record.error_messages ?? undefined}
                messageType={INVOICE_FILE_MESSAGE_TYPES}
                receiptType={value.receipt_type || undefined}
              />
            </div>
          );
        },
        width: `${styles['column_invoice_file_width']}px`,
      },
      // 15. Action (3-dots menu + 詳細 link)
      {
        key: 'action',
        title: '',
        render(_, record) {
          return (
            <ActionCell
              record={record}
              onCancel={onCancel}
              onDelete={onDelete}
            />
          );
        },
        width: `${styles['column_action_width']}px`,
        ellipsis: false,
        align: 'center',
        fixed: 'right',
      },
    ],
    [
      selectedRowKeys,
      onSelectAll,
      isAllSelected,
      isAllDisabled,
      isLoading,
      columnWidths,
      onCancel,
      onDelete,
      t,
      container,
      sorterConfig,
      handleCheckboxChange,
      onStopPropagation,
      getCheckboxTooltipTitle,
    ]
  );

  return allColumns;
};
